import { describeRef, eventWhen } from '../describe'

/**
 * Suggestions for the editor's comboboxes: refs (`$role`, `$role.feet`, `$role.socket.hand`,
 * `spot:left`), event refs (`thrower:release`) and state values — each with its plain-words
 * reading as the option label.
 */

export interface RefOption {
  value: string
  label: string
}

/** Stage spots every scene has. */
export const SPOTS = ['left', 'middle', 'right'] as const
/** Points of a participant or a place (§11.4 refs). */
export const POINTS = ['feet', 'top', 'center', 'edge', 'surface', 'pos'] as const
/** Moments a clip or a toss marks (`role:moment`), when the server does not list them. */
export const EVENTS = ['release', 'grab', 'contact', 'arrive', 'land'] as const

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

export function refOptions(roles: readonly string[], sockets: readonly string[], opts: { faces?: boolean; roleOnly?: boolean } = {}): RefOption[] {
  const out: RefOption[] = []
  if (opts.faces) for (const f of ['left', 'right', 'camera']) out.push({ value: f, label: `Facing ${f}` })
  for (const r of roles) {
    out.push({ value: `$${r}`, label: cap(describeRef(`$${r}`)) })
    if (opts.roleOnly) continue
    for (const p of POINTS) out.push({ value: `$${r}.${p}`, label: cap(describeRef(`$${r}.${p}`)) })
    for (const s of sockets) out.push({ value: `$${r}.socket.${s}`, label: cap(describeRef(`$${r}.socket.${s}`)) })
  }
  if (!opts.roleOnly) for (const s of SPOTS) out.push({ value: `spot:${s}`, label: cap(describeRef(`spot:${s}`)) })
  return out
}

export function eventOptions(roles: readonly string[], moments: readonly string[] = EVENTS): RefOption[] {
  return roles.flatMap((r) => moments.map((e) => ({ value: `${r}:${e}`, label: cap(eventWhen(`${r}:${e}`)) })))
}

const MOODS = ['happy', 'sad', 'surprised', 'sleepy', 'scared', 'excited']

/** Values a state key usually takes. */
export function valueOptions(roles: readonly string[], key?: string): RefOption[] {
  const refs = roles.map((r) => ({ value: `$${r}`, label: cap(describeRef(`$${r}`)) }))
  const base = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
    { value: 'none', label: 'Nothing / no one' },
    { value: 'any', label: 'Anything / anyone' },
  ]
  if (key === 'mood') return [...MOODS.map((m) => ({ value: m, label: m })), ...base]
  if (key === 'facing') return ['left', 'right', 'camera'].map((f) => ({ value: f, label: `Facing ${f}` }))
  return [...refs, ...base, ...MOODS.map((m) => ({ value: m, label: m }))]
}
